/**
 * Token Storage Utility
 * Platform-aware token persistence for dual auth (cookies + Bearer tokens).
 *
 * - Web: token lives in a module-scoped variable only (never localStorage).
 *   The httpOnly cookie remains the persistent auth mechanism.
 * - Native (Capacitor): token is also persisted via the Capacitor Preferences
 *   so it survives app restarts (cookies are unreliable on native).
 */
import { Capacitor } from '@capacitor/core';

const STORAGE_KEY = 'eqaya_auth_token';

let _token = null;

const getNativePreferences = () => Capacitor?.Plugins?.Preferences || null;

/**
 * Returns true when running inside a Capacitor native shell (iOS/Android).
 */
export const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/**
 * Store the auth token. On native platforms the token is also written to
 * Capacitor Preferences (encrypted at rest on iOS Keychain / Android
 * EncryptedSharedPreferences).
 */
export async function setToken(token) {
  _token = token;
  if (isNative() && token) {
    const Preferences = getNativePreferences();
    if (Preferences) {
      await Preferences.set({ key: STORAGE_KEY, value: token });
    }
  }
}

/**
 * Clear the auth token from memory and, on native, from persistent storage.
 */
export async function clearToken() {
  _token = null;
  if (isNative()) {
    const Preferences = getNativePreferences();
    if (Preferences) {
      await Preferences.remove({ key: STORAGE_KEY });
    }
  }
}

/**
 * Synchronous read of the in-memory token. Used by request interceptors.
 */
export function getToken() {
  return _token;
}

/**
 * Hydrate the in-memory token from persistent storage on app startup.
 * On web this is a no-op (returns null); on native it reads from Preferences.
 */
export async function loadToken() {
  if (isNative()) {
    const Preferences = getNativePreferences();
    if (Preferences) {
      const { value } = await Preferences.get({ key: STORAGE_KEY });
      _token = value;
    }
  }
  return _token;
}
